import { getClrGradient } from '@gto/helpers/colors'
import { getCorrectnessActionGroups, all_streets } from '@/helpers/constants'
import { getRaisesNonRaises } from '@/helpers/grouping'
import { numRoundTo5bb } from '@/helpers/helpers'
import { numFixedUnit } from '@gto/helpers/number-formatting'
import { getStacksFromDepth } from '@/store/gameformat/gameformat'
import { getCardsByStreet } from '@/helpers/cards'
import { v4 as uuid } from 'uuid'
import { omit } from 'lodash'
import findLast from 'lodash/findLast'

function getCssRaiseClass(raise_group) {
  const conf = {
    bet_overbet: 'R4',
    bet_large: 'R3',
    bet_medium: 'R2',
    bet_small: 'R1',
  }
  const res = conf[raise_group]
  return res
}
function getActionStyles({ available_actions }) {
  let aa = available_actions
  const groupsTracker = {
    bet_overbet: 0,
    bet_large: 0,
    bet_medium: 0,
    bet_small: 0,
  }
  aa.forEach(a => {
    if (a.raise_group) {
      groupsTracker[a.raise_group] += 1
    }
  })
  let ratioTracker = {
    bet_overbet: 0,
    bet_large: 0,
    bet_medium: 0,
    bet_small: 0,
  }

  const aTypes = {
    FOLD: 'F',
    CALL: 'C',
    CHECK: 'X',
  }

  aa = aa.map(a => {
    // CALL action can have code RAI - action.code is for API only
    const css_class = a.raise_group ? getCssRaiseClass(a.raise_group) : aTypes[a.type]

    const css_var = `--clr-${css_class}`

    let res = {
      css_var,
      css_class,
      hex: '',
      // hex_default: '',
    }

    if (a.raise_group) {
      let hex = getComputedStyle(document.body).getPropertyValue(`${css_var}-initial`)
      const group = a.raise_group

      const idx = groupsTracker[group] - 1
      let part = groupsTracker[group] && idx > 0 ? 1 / idx : 0
      part = part || 0

      const gradientX = part * ratioTracker[group]
      ratioTracker = { ...ratioTracker, [group]: ratioTracker[group] + 1 }

      const clr1 = hex
      const clr2 = getComputedStyle(document.body).getPropertyValue(`${css_var}_2-initial`)

      hex = getClrGradient({
        clr1,
        clr2,
        xaxis: gradientX,
      })

      res = {
        ...res,
        hex,
      }
    } else {
      const hex = getComputedStyle(document.body).getPropertyValue(css_var)
      res = {
        ...res,
        hex,
      }
    }

    const r = {
      ...a,
      ...res,
    }
    return r
  })

  return aa
}

function getAvailableActions({ available_actions, game, selected_action_code }) {
  // console.log(Date.now(), '00')
  const street = game?.current_street?.type?.toLowerCase()
  const raise_alias = game?.bet_display_name === 'BET'

  const l = available_actions.length - 1
  const correctnessActionGroups = getCorrectnessActionGroups()
  let res = available_actions.map((action, idx) => {
    const aa = action.action ? { ...action, ...action.action } : action

    const betsize = Number(aa.betsize) || 0
    const isRaise = aa.type === 'RAISE'
    const raise_group = isRaise ? (aa.advanced_group || aa.raise_group).toLowerCase() : null
    const raise_amount = isRaise ? Number(aa.betsize) : null

    // betsize_by_pot used in nodeedit popup - if we show this on preflop use amount_by_pot instead there
    const betsize_by_pot =
      isRaise && street !== 'preflop'
        ? numFixedUnit(Number(aa.betsize_by_pot) * 100, 'bb_pot')
        : null
    const amount_by_pot = betsize_by_pot

    // isRaise && street !== 'preflop' ? Number((Number(aa.betsize_by_pot) * 100).toFixed(2)) : null
    const display_name =
      aa.display_name.toLowerCase().charAt(0).toUpperCase() + aa.display_name.toLowerCase().slice(1)
    const correctness = (aa.correctness || '').toLowerCase()
    const correctness_group = correctness ? correctnessActionGroups[correctness] : null

    const { ev } = aa
    const { ev_loss } = aa
    const { gto_score } = aa
    const { frequency } = aa
    const { total_frequency } = aa
    const { practice_action_id } = aa

    const { is_hand_end } = aa
    const { is_solution_end } = aa
    const is_fe_solution_end = aa.is_solution_end && !aa.is_hand_end
    const { is_showdown } = aa

    const { real_raise_amount } = aa
    const { real_amount_by_pot } = aa

    // is_hand_end - end of hand for hero (including regular end) >> showdown
    // is_solution_end - no posflop solution >> showdown
    // is_showdown - real-life showdown >> all board dealt >> showdown
    const outcome =
      is_hand_end || is_solution_end || is_showdown
        ? 'showdown'
        : aa.next_street
          ? 'board'
          : 'next_player'

    const is_selected = selected_action_code === aa.code

    const r = {
      code: aa.code,
      type: aa.type,
      display_name,
      ...(betsize ? { betsize } : {}),

      betsize_by_pot,
      ...(isRaise ? { raise_alias } : {}),
      ...(isRaise ? { allin: aa.allin } : {}),
      ...(raise_group ? { raise_group } : {}),
      ...(raise_amount ? { raise_amount } : {}),
      ...(amount_by_pot ? { amount_by_pot } : {}),

      ...(real_raise_amount ? { real_raise_amount } : {}),
      ...(real_amount_by_pot ? { real_amount_by_pot } : {}),

      ...(correctness ? { correctness } : {}),
      ...(correctness_group ? { correctness_group } : {}),
      ...(ev || ev === 0 ? { ev } : {}),
      ...(ev_loss || ev_loss === 0 ? { ev_loss } : {}),
      ...(gto_score || gto_score === 0 ? { gto_score } : {}),
      ...(frequency || frequency === 0 ? { frequency } : {}),
      ...(total_frequency || total_frequency === 0 ? { total_frequency } : {}),
      ...(practice_action_id ? { practice_action_id } : {}),

      is_fe_solution_end,
      is_hand_end,
      is_solution_end,
      is_showdown,
      outcome,
      // isNa,

      can_ai_solve: aa.can_be_solved_by_ai || null,

      order: idx,
      order_reverse: l - idx,
      is_selected,
      is_preselected: is_selected,
      next_position: aa.next_position || null,
    }
    return r
  })
  res = getActionStyles({ available_actions: res })
  return res
}
function getTrainerSliderActions({ available_actions, selected_action }) {
  let { raises, nonRaises } = getRaisesNonRaises({ actions: available_actions })
  // console.log(bestBetEv, betsEv)
  nonRaises = nonRaises.sort((a, b) => (a.order_reverse < b.order_reverse ? -1 : 1))
  let res = []
  if (selected_action) {
    const betsFreq = raises.reduce((a, b) => a + b.frequency, 0)
    res = [...(raises.length ? [{ ...raises[0], css_class: 'R1' }] : []), ...nonRaises]
    res = res.map(i => {
      const r = {
        ...i,
        frequency: i.type === 'RAISE' ? betsFreq : i.frequency,
      }
      return r
    })
    // debugger
  } else {
    const betsGtoScores = raises.map(a => a.gto_score)
    const bestBetScore = Math.max(...betsGtoScores)
    let bestBet = raises.find(i => i.gto_score === bestBetScore)
    if (raises.length > 1) {
      bestBet = {
        ...{ ...bestBet, css_class: 'R1' },
        display_name: raises.find(i => !i.allin)?.display_name || bestBet.display_name,
      }
    }
    res = [...(raises.length ? [bestBet] : []), ...nonRaises]
    const part = 1 / res.length
    res = res.map((i, idx) => {
      const r = {
        ...i,
        frequency: part,
      }
      return r
    })
  }
  // res[0].is_preselected = true
  // console.log('getTrainerSliderActions', res)
  return res
}

function getPokerPositions({ gameformat }) {
  let players = []
  // console.log(gameformat)
  if (!gameformat) {
    return ['OOP', 'IP']
  }
  switch (gameformat.players) {
    case 0:
      // This exists solely for a state before a custom report is generated
      players = []
      break
    case 2:
      players = ['SB', 'BB']
      break
    case 3:
      players = ['BTN', 'SB', 'BB']
      break
    case 4:
      players = ['CO', 'BTN', 'SB', 'BB']
      break
    case 5:
      players = ['HJ', 'CO', 'BTN', 'SB', 'BB']
      break
    case 6:
      if (gameformat.cash_type === 'ante_straddle' || gameformat.cash_type === 'straddle') {
        return ['UTG', 'CO', 'BTN', 'SB', 'BB', 'STR']
      }
      if (
        gameformat.format === 'chipev' ||
        gameformat.format === 'icm' ||
        gameformat.format === 'icm_events'
      ) {
        return ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      }
      players = ['UTG', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      break
    case 7:
      players = ['UTG', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      break
    case 8:
      if (gameformat.cash_type === 'ante_straddle' || gameformat.cash_type === 'straddle') {
        players = ['UTG', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB', 'STR']
      } else {
        players = ['UTG', 'UTG+1', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      }
      break
    case 9:
      players = ['UTG', 'UTG+1', 'UTG+2', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      break
    case 10:
      players = ['UTG', 'UTG+1', 'UTG+2', 'UTG+3', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      break
    default:
      console.warn('!!!!! - No such player option', `${players}`)
      players = ['UTG', 'HJ', 'CO', 'BTN', 'SB', 'BB']
      break
  }
  return players
}
function getNextHeroPosition({ hero, positions, step = 1 }) {
  const heroIdx = positions.findIndex(i => i === hero)
  let res = positions[heroIdx + step]
  res = !res ? positions[step === 1 ? 0 : positions.length - 1] : res
  return res
}

function isDealer({ position, players }) {
  players = players.map(p => p.position || p)
  return players.includes('BTN') ? position === 'BTN' : position === 'SB'
}

function getAfterStreet({ street }) {
  const res = street === 'preflop' ? 'flop' : street === 'flop' ? 'turn' : 'river'
  return res
}

function getNextStreet({ street, selected_action }) {
  let next_street = street
  if (selected_action?.outcome === 'board') {
    next_street = getAfterStreet({ street: next_street })
  }
  return { next_street }
}

function getChipNominations({ amount: chipsOnTable, nominations = [100, 50, 25, 5, 1, 0.5] }) {
  const remainder = chipsOnTable % nominations[nominations.length - 1]
  chipsOnTable +=
    remainder < nominations[nominations.length - 1] / 2
      ? -remainder
      : nominations[nominations.length - 1] - remainder
  const chips = []
  nominations.forEach(nomination => {
    if (nomination > chipsOnTable) return
    const amount = Math.floor(chipsOnTable / nomination)
    chipsOnTable %= nomination
    chips.push({
      nomination,
      amount,
    })
  })
  return chips
}

function getNextPot({ players, pot, start_pot, selected_action }) {
  let next_pot = 0
  let next_start_pot = start_pot
  players.forEach(p => {
    const playerBet = p.stack - (p.next_current_stack ?? p.current_stack)
    next_pot += playerBet
  })
  if (
    selected_action?.outcome === 'board'
    // || selected_action?.outcome === 'showdown'
  ) {
    next_start_pot = next_pot
  }
  // const next_pot_nomination = getChipNominations({ amount: next_pot })
  return {
    next_pot,
    next_start_pot,
    // next_pot_nomination
  }
}

function getPlayers({ players, hero_position, selected_action }) {
  // console.log('getPlayers', players, hero_position, betsize)
  const res = players.map(p => {
    const is_folded = p.is_folded || null
    const chips_on_table = Number(p.chips_on_table || 0)
    const current_stack = Number(p.current_stack || 0)
    const stack = Number(p.stack || 0)
    const bounty = Number(p.bounty || 0)
    const bounty_in_bb = Number(p.bounty_in_bb || 0)
    const chips_on_table_nomination = p.chips_grouped_by_nomination || null
    // let chips_on_table_nomination = p.chips_on_table_nomination || null

    let next_is_folded = is_folded
    let next_chips_on_table = chips_on_table
    let next_current_stack = current_stack
    let next_chips_on_table_nomination = chips_on_table_nomination
    if (p.position === hero_position && selected_action) {
      if (selected_action.type === 'FOLD') {
        next_is_folded = true
      } else if (selected_action.type === 'RAISE' || selected_action.type === 'CALL') {
        const { betsize } = selected_action
        next_chips_on_table = betsize
        next_current_stack = current_stack - (betsize - chips_on_table)
        next_chips_on_table_nomination = getChipNominations({ amount: next_current_stack })
      }
    }

    const { position } = p
    const r = {
      position,
      stack,

      chips_on_table,
      current_stack,
      next_chips_on_table,
      next_current_stack,
      chips_on_table_nomination,
      next_chips_on_table_nomination,
      bounty,
      bounty_in_bb,

      hand: p.hand || '',
      is_dealer: p.is_dealer ?? isDealer({ position, players }), // players in custo solutions can have any position what you want
      is_folded,
      next_is_folded,
      is_hero: p.is_hero || null,
      is_active: p.is_active || null,
      relative_postflop_position: p.relative_postflop_position || null,
    }
    return r
  })
  // console.log(res)
  return res
}

// function getPlayersDefault({ isCustomSolution, gameformat }) {
function getPlayersDefault({ customTree, gameformat }) {
  let display_positions = []
  let stacks = []

  // console.log(customTree)
  // debugger
  if (customTree) {
    // positions = customTree.players.map(p => p.display_position)
    const positions = customTree.players.length === 3 ? ['OOP', 'OOP+1', 'IP'] : ['OOP', 'IP']
    display_positions = positions.map(
      position =>
        customTree.players.find(p => p.position === position)?.display_position || position,
    )
    stacks = customTree.players.map(p => p.stack)
  } else {
    display_positions = getPokerPositions({ gameformat })
    stacks = gameformat.stacks
      ? gameformat.stacks.split('-')
      : getStacksFromDepth({
          depth: gameformat.stack_rounding || gameformat.depth,
          totalNum: gameformat.players,
        })
  }

  const players = display_positions.map((p, idx) => {
    const stack = numRoundTo5bb(Number(stacks[idx]))
    return {
      position: p,
      stack,
      current_stack: stack,
      bounty: gameformat.bounties ? gameformat.bounties[idx] : null,
    }
  })
  const res = getPlayers({ players, hero_position: null, selected_action: null })
  return res
}

function setActiveHistorySpot({ history_spots, spot_id }) {
  const res = history_spots.map(i => ({
    ...i,
    is_active: i.id === spot_id,
  }))
  return res
}

function getHistorySpots({
  history_spots,
  hasIsReal = false,
  hasIsExtra = false,
  hasSlider = false,
  hasRng = false,
  hasSelectedAction = false,
}) {
  const hh = history_spots.map((i, idx) => {
    const position = i.game.active_position
    const street = i.game.current_street.type.toLowerCase()
    const board = i.game.board || ''
    const raise_alias = i.game.bet_display_name === 'BET'
    const pot_odds = Number(i.game.pot_odds)

    const { preset_action_code } = i

    let selected_action = null
    let selected_action_code = ''

    if (i.selected_action) {
      // hh analyzer, bot-action
      selected_action_code = i.selected_action.code
    } else {
      const action = i.available_actions.find(aa => aa.selected)
      selected_action_code = action ? (action.action || action).code : null
    }

    const available_actions = getAvailableActions({
      available_actions: i.available_actions,
      game: i.game,
      selected_action_code,
    })

    selected_action = available_actions.find(aa => aa.is_selected) || null
    const selected_action_outcome = !selected_action ? null : selected_action.outcome

    const slider_actions = hasSlider
      ? getTrainerSliderActions({
          available_actions,
          selected_action,
        })
      : null

    const players = getPlayers({
      players: i.game.players,
      hero_position: position,
      selected_action,
    })

    const activePlayer = players.find(p => p.position === position)

    const pot = Number(i.game.pot)
    const start_pot = Number(i.game.current_street.start_pot)

    const {
      next_pot,
      next_start_pot,
      // next_pot_nomination
    } = getNextPot({ players, pot, start_pot, selected_action })
    const { next_street } = getNextStreet({ street, selected_action })

    const { is_real } = i // hhv1
    const { is_extra } = i // hhv1
    // const has_valid_solution = i.has_valid_solution || null
    const r = {
      id: idx,
      uid: i.uid || uuid(),
      is_active: false,

      custom_solution_id: i.custom_solution_id || null,
      available_node_edits: i.available_node_edits || null,

      position,
      players,
      street,
      pot_odds,
      pot,
      start_pot,
      next_street,
      next_pot,
      next_start_pot,

      raise_alias,

      // is_edited:i.is_edited,
      is_node_locked: i.is_node_locked,
      merged_actions: i.merged_actions,

      forced_fold: i.forced_fold,

      bounty: activePlayer?.bounty_in_bb && {
        bb: numFixedUnit(Number(activePlayer.bounty_in_bb), 'bb'),
        usd: numFixedUnit(Number(activePlayer.bounty), 'bounty'),
      },

      board,
      available_actions,
      ...(slider_actions ? { slider_actions } : {}),

      ...(preset_action_code ? { preset_action_code } : {}),
      selected_action_code,
      selected_action_outcome,
      ...(hasSelectedAction ? { selected_action } : {}),

      ...(hasIsReal ? { is_real } : {}),
      ...(hasIsExtra ? { is_extra } : {}), // hhv1
      ...(hasRng ? { rng: i.rng } : {}),
    }
    return r
  })
  return hh
}

// this function needsto be renamed it does more than just settings the type
// used for
// - setting type
// - resetting id after appending bot spot
// - setting showdown
// needs to be standalone cos it is used as a final check after appending bot spots
function setHistoryEventType({ history_spots }) {
  const hh = history_spots.map((i, idx) => {
    const previousEvent = history_spots[idx - 1] || null
    const type = previousEvent?.selected_action_outcome === 'showdown' ? 'showdown' : 'player'
    const selected_action_outcome = type === 'showdown' ? '' : i.selected_action_outcome

    const r = {
      ...i,
      id: idx,
      is_bot: !!i.is_bot,
      type,
      selected_action_outcome,
    }
    return r
  })
  return hh
}

function setHsPresetSelectedActions({ history_spots, spot }) {
  const res = history_spots.map(e => {
    if (e.id < spot.id && !e.selected_action_code) {
      const available_actions = e.available_actions.map(aa => ({
        ...aa,
        is_selected: aa.code === e.preset_action_code,
      }))

      const selectedAction = available_actions.find(aa => aa.is_selected) || null

      const r = {
        ...e,
        available_actions,
        selected_action_code: selectedAction.code,
        selected_action_outcome: selectedAction.outcome,
        preset_action_code: null,
      }
      return r
    }
    return e
  })
  return res
}

function setHsSelectedAction({
  history_spots,
  spot,
  action,
  key = 'is_selected',
  getNext = false,
}) {
  if (!spot) {
    return history_spots
  }

  let hspot = history_spots[spot.id]
  const available_actions = hspot.available_actions.map(aa => {
    const r = {
      ...aa,
      [key]: aa.code === action.code,
    }
    if (key === 'is_selected') {
      r.is_preselected = r.is_selected
    }
    return r
  })

  if (getNext) {
    const selected_action = available_actions.find(aa => aa[key]) || null
    const players = getPlayers({
      players: hspot.players,
      hero_position: hspot.position,
      selected_action,
    })

    const { pot } = hspot
    const { start_pot } = hspot
    const { street } = hspot
    const { next_pot, next_start_pot } = getNextPot({ players, pot, start_pot, selected_action })
    const { next_street } = getNextStreet({ street, selected_action })

    const applyAction = key === 'is_selected'
    hspot = {
      ...hspot,
      next_start_pot,
      next_pot,
      next_street,
      players,
      available_actions,
      ...(applyAction ? { selected_action_code: action.code } : {}),
      ...(applyAction ? { selected_action_outcome: action.outcome } : {}),
      ...(applyAction ? { preset_action_code: null } : {}),
    }
  } else {
    hspot = {
      ...hspot,
      available_actions,
    }
  }

  history_spots[spot.id] = hspot
  return history_spots
}

function unsetHsSelectedAction({ history_spots, spot }) {
  if (!spot) {
    return history_spots
  }

  let hspot = history_spots[spot.id]
  const available_actions = hspot.available_actions.map(aa => ({
    ...aa,
    is_selected: false,
    is_preselected: false,
  }))

  const players = getPlayers({
    players: hspot.players,
    hero_position: null,
    selected_action: null,
  })

  hspot = {
    ...hspot,
    players,
    available_actions,
    selected_action_code: null,
    selected_action_outcome: null,
    preset_action_code: null,
  }
  history_spots[spot.id] = hspot
  return history_spots
}

function getEventsFromHistoryApi({ future_spots = [], history_spots = [], custom_solution_id }) {
  const allEvents = [...history_spots, ...(future_spots || [])]
  let hh = getHistorySpots({ history_spots: allEvents })
  hh = setHistoryEventType({ history_spots: hh })

  if (custom_solution_id) {
    hh = hh.map(i => ({
      ...i,
      custom_solution_id,
    }))
  }

  return {
    history_spots: hh,
  }
}
function getEventsNextActionsApi({ future_spots, next_actions, history_spots }) {
  const allEvents = [next_actions, ...(future_spots || [])]
  let hh = getHistorySpots({ history_spots: allEvents })
  hh = [...history_spots, ...hh]
  hh = setHistoryEventType({ history_spots: hh })
  return {
    history_spots: hh,
  }
}

function getSpotSelectedAction({ spot }) {
  if (!spot) return null
  return (spot.available_actions || []).find(aa => aa.is_selected)
}
function getFlopShowdownNode({ action, currentSpot, history_spots }) {
  let hSpots = history_spots

  const lastSpot = hSpots[hSpots.length - 1]
  lastSpot.selected_action_outcome = 'solution_end'
  lastSpot.selected_action_code = action.code
  lastSpot.available_actions = lastSpot.available_actions.map(i =>
    i.code === action.code ? { ...i, is_selected: true } : i,
  )

  // let has_ai_prefill = !!(lastSpot?.available_actions || []).find(aa => aa.is_selected && aa.can_ai_solve)
  const solEndNode = {
    players: currentSpot.players,
    street: 'flop',
    available_actions: [],
    type: 'showdown',
    // type: 'solution_end',
    id: history_spots.length,
    // has_ai_prefill,
  }
  hSpots = [...hSpots, solEndNode]
  // hSpots[hSpots.length - 2].selected_action_outcome = 'solution_end'
  return { history_spots: hSpots }
}
// next_actions,
function getEventsTreePreviewApi({ future_spots, history_spots }) {
  const allEvents = [...history_spots, ...(future_spots || [])]
  let hh = getHistorySpots({ history_spots: allEvents })
  hh = setHistoryEventType({ history_spots: hh })
  return {
    history_spots: hh,
  }
}
function getEventsPracticeBotApi({ api_practice_bot, history_spots }) {
  // let available_actions = getPracticeBotAvailableActions({ api_practice_bot })
  const available_actions = api_practice_bot.actions.map(a => ({
    ...a.action,
    next_position: a.next_position,
    frequency: a.frequency,
    is_solution_end: a.is_solution_end,
  }))

  let selected_action = api_practice_bot.actions.find(a => a.selected)
  // let selected_action = api_practice_bot.actions.find(a => a.action.type === 'RAISE')
  // let selected_action = api_practice_bot.actions.find(a => a.action.type === 'FOLD')
  // let selected_action = api_practice_bot.actions.find(a => a.action.type === 'CHECK')
  // let selected_action = api_practice_bot.actions.find(a => a.action.type === 'CALL')

  selected_action = selected_action.action
  const botSpot = {
    available_actions,
    game: api_practice_bot.game,
    selected_action,
    custom_solution_id: api_practice_bot.custom_solution_id,
  }

  // debugger

  const botEvents = [botSpot]
  let hh_bot = getHistorySpots({ history_spots: botEvents, hasSlider: true })
  let hSpots = history_spots

  const lastSpot = history_spots[history_spots.length - 1]
  if (lastSpot && !lastSpot?.selected_action_code) {
    hSpots = hSpots.slice(0, -1)
  }

  hh_bot = [...hSpots, ...hh_bot]
  hh_bot = setHistoryEventType({ history_spots: hh_bot })

  const hSpot = hh_bot.length - 1
  const event = hh_bot[hSpot]
  selected_action = event.available_actions.find(a => a.is_selected)
  hh_bot[hSpot] = {
    ...event,
    selected_action_code: selected_action.code,
    selected_action_outcome: selected_action.outcome,
    is_bot: true,
  }

  return {
    history_spots: hh_bot,
    history_spot: hh_bot[hh_bot.length - 1],
    selected_action,
  }
}

function getPracticeAvailableActions({ api_practice }) {
  const { has_valid_solution, solutions } = api_practice
  const vs = has_valid_solution
  const available_actions = solutions.map((a, idx) => {
    const r = {
      // ...a,
      ...a.action,
      correctness: vs ? a.correctness : 'na',
      ev: vs ? a.ev : 0,
      ev_loss: vs ? a.ev_loss : 0,
      gto_score: vs ? a.gto_score : 0,
      frequency: vs ? a.frequency : 0,
      total_frequency: vs ? a.full_solution.total_frequency : 0,
      next_position: a.next_position,
      practice_action_id: a.id,
      is_solution_end: a.is_solution_end,
    }
    return r
  })
  // console.log(available_actions)
  // debugger
  return available_actions
}

function getEventsPracticeSpecificSpotApi({ api_practice, history_spots, history_spot }) {
  // const next_position = api_practice.next_position
  // api_practice = getApiPreflopPracticeSolutionData(api_practice)
  // api_practice = getApiPreflopSolutionData(api_practice)
  let available_actions = getPracticeAvailableActions({ api_practice })
  available_actions = getAvailableActions({
    available_actions,
    game: api_practice.game,
    selected_action_code: history_spot.selected_action_code,
  })
  // custom_solution_id: api_practice.custom_solution_id,
  // debugger

  const slider_actions = getTrainerSliderActions({ available_actions, selected_action: null })

  const hs = history_spots.map(h => {
    if (history_spot.id === h.id) {
      return {
        ...h,
        // has_valid_solution: api_practice.has_valid_solution,
        available_actions,
        slider_actions,
        api_practice,
        rng: api_practice.rng,
        uid: uuid(),
      }
    }
    return h
  })
  // debugger
  return {
    history_spots: hs,
  }
}
function getEventsPracticeFullhandApi({ api_practice, history_spots }) {
  // api_practice = getApiPreflopPracticeSolutionData(api_practice)
  // api_practice = getApiPreflopSolutionData(api_practice)

  const available_actions = getPracticeAvailableActions({ api_practice })
  const spot = {
    available_actions,
    game: api_practice.game,
    custom_solution_id: api_practice.custom_solution_id,
  }
  // console.log(spot)
  // debugger
  const hh = getHistorySpots({
    history_spots: [spot],
    hasSlider: true,
    // hasGroupedActions: true,
    // hasSimpleActions: true,
  })
  const hSpot = {
    ...hh[0],
    api_practice,
    // has_valid_solution: api_practice.has_valid_solution,
    rng: api_practice.rng,
  }

  let hSpots = history_spots
  const lastSpot = history_spots[history_spots.length - 1]
  if (lastSpot && !lastSpot?.selected_action_code) {
    hSpots = hSpots.slice(0, -1)
  }

  hSpots = [...hSpots, hSpot]
  // console.log(hSpots)

  hSpots = setHistoryEventType({ history_spots: hSpots })

  return {
    history_spots: hSpots,
  }
}
function getEventsShowdownApi({
  game,
  history_spots,
  custom_solution_id,
  // actionOutcome,
  // hero_position,
}) {
  const next_action = {
    game,
    available_actions: [],
    custom_solution_id,
  }
  // debugger
  const allEvents = [next_action]
  let hSpots = getHistorySpots({
    history_spots: allEvents,
  })

  hSpots = [...history_spots, ...hSpots]
  const l = hSpots.length
  // hSpots[l - 2].selected_action_outcome = actionOutcome || hSpots[l - 2].selected_action_outcome
  hSpots[l - 1].id = l - 1

  hSpots = setHistoryEventType({
    history_spots: hSpots,
    //  hero_position
  })
  // debugger
  return hSpots
}

function getBoardSpot({
  next_spot,
  street,
  // has_ai_prefill,
  board,
}) {
  street = street || next_spot?.street
  board = board || ''
  const streetCards =
    street === 'preflop'
      ? ''
      : street === 'flop'
        ? board.substring(0, 6) || 'E0E1E2'
        : street === 'turn'
          ? board.substring(6, 8) || 'E3'
          : board.substring(8, 10) || 'E4'
  // const type = path_name === 'PracticeSpecificSpot' ? 'filter' : 'board'
  const spot = {
    type: 'board',
    id: street,
    street,
    // has_ai_prefill,
    board: streetCards,
    players: next_spot?.players || [],
    start_pot: next_spot?.start_pot || 0,
    pot: next_spot?.pot || 0,
    stack_pot_ratio: next_spot?.stack_pot_ratio || '',
    custom_solution_id: next_spot?.custom_solution_id || '',
    is_filter: false,
  }
  return spot
}

// function getLastActionTaken({ history_spots, street }) {
//   const streetSpots = history_spots.filter(s => s.type === 'player' && s.street === street)
//   const lastStreetSpot = streetSpots[streetSpots.length - 1]
//   let lastActionTaken = lastStreetSpot.available_actions.find(aa => aa.is_selected)
//   return lastActionTaken
// }

function getCompleteHistorySpots({
  history_spots,
  board,
  addMissingBoardSpots = false,
  hasFirstBoardSpot = false,
  // addMissingBoardSpots = true,
  // isPreflop = true,
}) {
  const hSpots = history_spots.map(i => ({
    id: i.id,
    uid: i.uid,

    available_actions: i.available_actions,

    is_active: i.is_active,
    is_bot: i.is_bot,
    is_hero: i.is_hero,
    next_pot: i.next_pot,
    next_start_pot: i.next_start_pot,
    next_street: i.next_street,
    players: i.players,
    player: i.players.find(pp => pp.position === i.position),
    position: i.position,
    pot: i.pot,
    start_pot: i.start_pot,
    pot_odds: i.pot_odds,

    bounty: i.bounty,

    custom_solution_id: i.custom_solution_id,
    available_node_edits: i.available_node_edits,
    board: i.board,

    is_real: i.is_real, // hhv1

    // is_edited:i.is_edited,
    is_node_locked: i.is_node_locked,
    merged_actions: i.merged_actions,

    // https://app.dev.gtowizard.com/solutions?solution_type=gwiz&gametype=SpinsAnteGeneral&depth=16.17
    forced_fold: i.forced_fold,

    raise_alias: i.raise_alias,
    preset_action_code: i.preset_action_code,
    selected_action_code: i.selected_action_code,
    selected_action_outcome: i.selected_action_outcome, // board, player, showdown
    street: i.street,
    type: i.type,
    ...(i.boards ? { boards: i.boards } : {}),
    ...(i.urlParams ? { urlParams: i.urlParams } : {}),
    ...(i.selected_action ? { selected_action: i.selected_action } : {}),
  }))

  // let boardSpots = []
  let currentStreet = 'preflop'
  let res = []

  hSpots.forEach((hs, idx) => {
    res.push(hs)
    if (hs?.selected_action_outcome === 'board') {
      currentStreet = hs?.street

      const next_spot = hSpots[idx + 1] || { street: getAfterStreet({ street: currentStreet }) }
      const spot = getBoardSpot({
        next_spot,
        board,
        street: null,
      })
      currentStreet = spot?.street || 'preflop'
      res.push(spot)
    }
  })

  if (hasFirstBoardSpot) {
    const spot = getBoardSpot({
      next_spot: res[0],
      // has_ai_prefill: false,
      board,
      street: null,
    })
    res.splice(0, 0, spot)
  }

  // for trainer preflop only gmfs and game-mode:street >> do not show board from next-street
  const lastSpot = res[res.length - 1]
  if (lastSpot?.type === 'showdown' && lastSpot?.street !== 'river') {
    // hhAnalyzer is forced to end on river so full board is displayed
    const preShowdownSpot = res[res.length - 2]
    const preShowdownStreet = preShowdownSpot?.street

    const lastActionTaken = preShowdownSpot.available_actions.find(aa => aa.is_selected)
    if (lastActionTaken.is_showdown) {
      // show full board
      res[res.length - 1].street = 'river'

      // create missing board hSspots
      if (addMissingBoardSpots) {
        const currentBoardSpots = res.filter(e => e.type === 'board')
        const missingBoardSpots = all_streets.filter(
          i => !currentBoardSpots.some(e => e.street === i),
        )
        if (missingBoardSpots.length) {
          const lastBoardSpot = findLast(res, i => i.type === 'board')

          const completedBoardSpots = missingBoardSpots.map(street => {
            const streetBoard = getCardsByStreet(board, street)
            const rr = {
              ...lastBoardSpot,
              type: 'board',
              board: streetBoard,
              id: street,
              street,
              urlParams: null,
            }
            return rr
          })

          const showdownSpot = lastSpot
          res = res.filter(i => i.type !== 'showdown')

          res = [...res, ...completedBoardSpots, showdownSpot]
        }
      }
    } else {
      // to show board from finished street set board-spot or showdown-spot street to last street
      res[res.length - 1].street = preShowdownStreet || lastSpot.street
    }
  }
  // console.log('getCompleteHistorySpots', res)
  return res
}
function getGroupFoldHistorySpots({ history_spots, hero_position, onlyRealFolds = true }) {
  let folds = []
  const foldGroups = []

  const playersThatPlayed = []

  // hero-folds are not squashed
  history_spots.forEach((ee, idx) => {
    if (
      ee.position !== hero_position &&
      ee.selected_action_code === 'F' &&
      !playersThatPlayed.some(p => p === ee.position)
    ) {
      folds.push(ee)
    } else {
      playersThatPlayed.push(ee.position)
      if (folds.length) {
        if (folds.length === 1) {
          foldGroups.push(folds[0])
        } else {
          foldGroups.push(folds)
        }
      }
      folds = []
      foldGroups.push(ee)
    }

    if (idx === history_spots.length - 1 && folds.length) {
      if (folds.length === 1) {
        foldGroups.push(folds[0])
      } else {
        foldGroups.push(folds)
      }
    }
  })

  let res = foldGroups.map((i, idx) => {
    if (i.length) {
      let foldSpots = i
      if (onlyRealFolds) {
        foldSpots = foldSpots.filter(f => f.is_real)
      }
      if (foldSpots.length === 1) {
        return foldSpots[0]
      }
      if (foldSpots.length > 1) {
        const ff = foldSpots
        return {
          type: 'grouped_folds',
          id: `gf_${ff[0].street}_${idx}`,
          street: ff[0]?.street,
          folds_length: ff.length,
          spots: ff,
          grouped_ids: ff.map(e => e.id),
          urlParams: ff[0]?.urlParams || null,
          is_hero: false,
          is_bot: true,
        }
      }
      // no is_real folds
      return null
    }
    return i
  })

  res = res.filter(i => i)
  // console.log(res)
  return res
}
function getHistoryUrlParams({ history_spots, includeShowdown = true }) {
  let historySpots = history_spots

  if (!includeShowdown) {
    historySpots = historySpots.filter(i => i.type !== 'showdown')
  }

  let res = historySpots.reduce(
    (result, event) => {
      const code = event.selected_action_code
      if (event.street === 'preflop') {
        result.preflop_actions = [...result.preflop_actions, code]
      } else if (event.street === 'flop') {
        result.flop_actions = [...result.flop_actions, code]
      } else if (event.street === 'turn') {
        result.turn_actions = [...result.turn_actions, code]
      } else if (event.street === 'river') {
        result.river_actions = [...result.river_actions, code]
      }
      return result
    },
    {
      preflop_actions: [],
      flop_actions: [],
      turn_actions: [],
      river_actions: [],
    },
  )
  function gp(arr) {
    return arr.filter(i => i).join('-')
  }
  res = {
    preflop_actions: gp(res.preflop_actions),
    flop_actions: gp(res.flop_actions),
    turn_actions: gp(res.turn_actions),
    river_actions: gp(res.river_actions),
  }
  return res
}

function getHSpotsUrlParamsFromSolvedSequece({ history_spots, board, game_points }) {
  // debugger
  function gp(arr) {
    return (arr || []).join('-')
  }
  let aaSequence = null
  const hSpots = history_spots.map((spot, idx) => {
    // some game_points can be without solved_action_sequence
    // (live street has none, fake actions added to make the solver work)
    // just omit first that are without and use the last one for live solved streets
    aaSequence = game_points[idx]?.solved_action_sequence || aaSequence
    let urlParams = {
      preflop_actions: gp(aaSequence?.preflop_actions),
      flop_actions: gp(aaSequence?.flop_actions),
      turn_actions: gp(aaSequence?.turn_actions),
      river_actions: gp(aaSequence?.river_actions),
    }
    const totalActions =
      (aaSequence?.preflop_actions || []).length +
      (aaSequence?.flop_actions || []).length +
      (aaSequence?.turn_actions || []).length +
      (aaSequence?.river_actions || []).length

    urlParams = {
      ...urlParams,
      ...(board ? { board } : {}),
      history_spot: totalActions,
    }
    const r = {
      ...spot,
      urlParams,
    }
    return r
  })
  return hSpots
}

function getHistorySpotsUrlParams({ history_spots, board }) {
  // debugger
  const hSpots = history_spots.map(spot => {
    // is_extra gamepoints should be ommited for url generation hhv1
    const hs = history_spots.filter(s => !s.is_extra && s.id < spot.id) // hhv1
    let urlParams = getHistoryUrlParams({ history_spots: hs })
    urlParams = {
      ...urlParams,
      ...(board ? { board } : {}),
      history_spot: hs.length,
    }
    const r = {
      ...spot,
      urlParams,
    }
    return r
  })
  return hSpots
}

function getActiveOpponents({ hero_position, players }) {
  let res = players.filter(p => !p.is_folded && p.position !== hero_position)
  res = res.map(p => p.position)
  return res
}

function getLastSpotsOpponent({ history_spots, gameformat, custom_tree }) {
  const lastSpot = history_spots[history_spots.length - 1]
  const activeOpponents = lastSpot.players.filter(i => (i.is_hero ? false : !i.is_folded))
  const oneOne = activeOpponents.length <= 1
  // debugger
  if (!oneOne) return null

  const opponentSpot = findLast(history_spots, i => i?.type === 'player' && i?.is_bot)
  let players = lastSpot?.players

  if (!players && !gameformat) {
    console.warn('getLastSpotsOpponent: no players and no gameformat')
    debugger
  } else {
    // debugger
    players = players || getPlayersDefault({ gameformat })
  }
  const res = (players || []).find(i => i.position === opponentSpot?.position)
  // debugger
  return res || null
}

function unsetHsBot({ history_spots }) {
  const res = history_spots.map(hs => ({
    ...hs,
    is_bot: false,
  }))
  return res
}

function removePracticeActionId({ history_spots }) {
  // debugger
  // console.log(history_spots)
  const res = history_spots.map(hs => ({
    ...hs,
    available_actions: hs.available_actions.map(aa => ({
      ...aa,
      practice_action_id: null,
    })),
  }))
  // console.log(res)
  return res
}
function stripActionCorrectness(action) {
  return omit(action, ['correctness', 'correctness_group'])
}

function setHsBot({ history_spots, hero_position }) {
  const res = history_spots.map(hs => {
    const isBot = hero_position !== hs.position
    let { available_actions } = hs
    if (hs.is_bot && available_actions.some(a => a.correctness)) {
      // previously heros available actions
      available_actions = available_actions.map(a => stripActionCorrectness(a))
    }
    const r = {
      ...hs,
      is_bot: isBot,
      available_actions,
      // players: hs.players.map(p => ({
      //   ...p,
      //   is_hero: hero_position === hs.position,
      //   is_bot: hero_position !== hs.position,
      // })),
    }
    return r
  })
  return res
}

// --------------

function getCurrentBetAsPot({ pot, latestStreetBet, latestActingPlayerBet, currentBet }) {
  const newPot = pot + latestStreetBet - latestActingPlayerBet
  const raiseSizeInBB = currentBet - latestStreetBet
  const currentBetAsPot = raiseSizeInBB / newPot
  return currentBetAsPot
}

function getCurrentBet({ pot, latestStreetBet, latestActingPlayerBet, currentBetAsPot }) {
  const newPot = pot + latestStreetBet - latestActingPlayerBet
  const raiseSizeInBB = newPot * currentBetAsPot
  const currentBet = latestStreetBet + raiseSizeInBB
  return currentBet
}

// pot  = current pot
// latestStreetBet = last bet on current street made by any player
// latestActingPlayerBet = last bet on current street made by acting player
// currentBet = the amount that hero wants to bet
function getCurrentBetAsPotFromHistory({
  history_spots,
  history_spot,
  street,
  currentBet,
  currentBetAsPot,
  blinds = { SB: 0, BB: 0 },
}) {
  const { pot } = history_spot

  const streetSpotsTillSpot = history_spots.filter(
    i => i.id <= history_spot.id && i.street === street,
  )

  const isRaiseCall = aa => aa.type === 'RAISE' || aa.type === 'CALL'

  const l_latestStreetBetSpot = streetSpotsTillSpot.findLast(
    i =>
      i.id < history_spot.id && i.available_actions.some(aa => aa.is_selected && isRaiseCall(aa)),
  )

  let latestStreetBet =
    (l_latestStreetBetSpot?.available_actions || []).find(aa => aa.is_selected && isRaiseCall(aa))
      ?.betsize || 0

  const l_latestActingPlayerBetSpot = findLast(
    streetSpotsTillSpot,
    i =>
      i.id < history_spot.id &&
      i.position === history_spot.position &&
      i.available_actions.some(aa => aa.is_selected && isRaiseCall(aa)),
  )

  let latestActingPlayerBet =
    (l_latestActingPlayerBetSpot?.available_actions || []).find(
      aa => aa.is_selected && isRaiseCall(aa),
    )?.betsize || 0

  if (street === 'preflop') {
    const { position } = history_spot // BB or SB
    const isSB = position === 'SB'

    const pos1 = isSB ? 'BB' : 'SB'
    const pos2 = isSB ? 'SB' : 'BB'

    latestStreetBet = latestStreetBet || Number(blinds[pos1] || 0)
    latestActingPlayerBet = latestActingPlayerBet || Number(blinds[pos2] || 0)

    // works on first preflop node
    // latestStreetBet = latestStreetBet || Number(blinds.SB || 0)
    // latestActingPlayerBet = latestActingPlayerBet || Number(blinds.BB || 0)
  }

  currentBetAsPot =
    currentBetAsPot ||
    getCurrentBetAsPot({ currentBet, pot, latestStreetBet, latestActingPlayerBet })
  currentBet =
    currentBet || getCurrentBet({ currentBetAsPot, pot, latestStreetBet, latestActingPlayerBet })

  const res = {
    currentBet,
    currentBetAsPot,
  }
  return res
}

export {
  getEventsFromHistoryApi,
  getEventsNextActionsApi,
  getCompleteHistorySpots,
  getHistoryUrlParams,
  setHistoryEventType,
  getHistorySpots,
  getAvailableActions,
  getTrainerSliderActions,
  getEventsPracticeBotApi,
  getEventsPracticeFullhandApi,
  getPracticeAvailableActions,
  getEventsPracticeSpecificSpotApi,
  getGroupFoldHistorySpots,
  getEventsShowdownApi,
  getHistorySpotsUrlParams,
  getHSpotsUrlParamsFromSolvedSequece,
  getBoardSpot,
  getPokerPositions,
  getPlayersDefault,
  getChipNominations,
  getActionStyles,
  setHsPresetSelectedActions,
  setHsSelectedAction,
  unsetHsSelectedAction,
  setActiveHistorySpot,
  getActiveOpponents,
  unsetHsBot,
  setHsBot,
  removePracticeActionId,
  getEventsTreePreviewApi,
  getNextHeroPosition,
  getLastSpotsOpponent,

  // getStreetIdx,
  getAfterStreet,
  getCurrentBetAsPot,
  getCurrentBetAsPotFromHistory,
  getFlopShowdownNode,
  getSpotSelectedAction,
}
